import type {PrerequisiteStatus} from '@/apiTypes/registration';
import type {Profile} from '@/apiTypes/profile';
import {markAttending} from '@/server/attendance';
import {registerForEvent} from '@/server/registration';
import {assign, fromPromise, raise, setup} from 'xstate';
import {hasRegistrationInfo} from './hasRegistrationInfo';

type Intent = 'register' | 'track';

type Context = {
  eventId: string;
  /**
   * Which CTA the user chose. Seeded by the route wrapper from the event's
   * source (`register` for native Wayra events, `track` for external events
   * where Wayra only records intent and hands off to the source platform).
   */
  intent: Intent;
  categoryId: string | null;
  routeId: string | null;
  profile: Profile | null;
  skipRiderFields: boolean;
  prerequisiteStatus: PrerequisiteStatus;
  /**
   * Mirrors `event.requiresClubMembership` — when true, registration is gated
   * on the rider being a current `OrganizerMember` of the event's organizer.
   */
  requiresClubMembership: boolean;
  /**
   * Loader-derived: `true` when the rider is a current member of the event's
   * organizer. Always `false` for unauthenticated viewers.
   */
  isMember: boolean;
  /**
   * Set to `true` after the user has acknowledged the prerequisite-confirm
   * modal so the server stamps the registration with
   * `PENDING_PREREQUISITE_REVIEW`.
   */
  offPlatformClaim: boolean;
};

type EventRegistrationEvent =
  | {type: 'REGISTER'}
  | {type: 'TRACK'}
  | {type: 'PROFILE_COMPLETED'}
  | {type: 'CANCEL_PROFILE'}
  | {type: 'PREREQUISITES_CONFIRMED'}
  | {type: 'CANCEL_PREREQUISITES'}
  | {type: 'REGISTRATION_CONFIRMED'}
  | {type: 'OPEN_HANDOFF'}
  | {type: 'CLOSE_HANDOFF'}
  | {type: 'SELECT_ROUTE'; routeId: string; categoryId: string | null}
  | {type: 'SELECT_CATEGORY'; categoryId: string};

/**
 * Registration server-call input — what `registerForEvent` actually needs,
 * derived from context.
 */
type RegisterInput = Pick<
  Context,
  'eventId' | 'categoryId' | 'routeId' | 'offPlatformClaim'
>;

/**
 * Track server-call input — `markAttending` only needs the event id; the
 * authenticated profile comes from server-side auth middleware.
 */
type TrackInput = Pick<Context, 'eventId'>;

/**
 * Models the UI state of the unified registration / track flow.
 *
 * The root is a parallel state with two regions:
 *
 *   - `flow` runs the actual submission lifecycle (idle → optional gating
 *     modals → submitting → refreshing → idle). The `intent` context value
 *     selects which actor `submitting` invokes: `register` for native Wayra
 *     events, `track` for external events where we only record attendance and
 *     hand the user off to the source platform.
 *   - `handoffModal` runs independently as `closed` ⇄ `open`. The track
 *     success path raises `OPEN_HANDOFF` after `notifySuccess`, and the
 *     handoff modal component dispatches `CLOSE_HANDOFF` when dismissed.
 *
 * Profile + skipRiderFields live in context (seeded at mount from loader /
 * auth). The machine decides whether the profile-completion modal is needed
 * — the sidebar just dispatches `REGISTER`. `hasRegistrationInfo` is the
 * single source of truth for "can skip the modal."
 *
 * The track path adds a `needsAuth` state for unauthenticated users: entry
 * stashes the pending track intent and the route wrapper redirects to login.
 * `needsAuth` is terminal for the lifetime of the actor — after sign-in the
 * page remounts a fresh machine in `flow.idle`, and the wrapper's mount-time
 * replay redispatches `TRACK` so the now-authenticated branch runs.
 *
 * After a successful submit the machine enters `refreshing` and waits for
 * the caller's `refreshData` actor (router loader invalidation) to resolve
 * before returning to `idle`. That avoids a visible flash between
 * `submitting` and the loader updating `isRegistered`.
 *
 * Action / actor stubs are no-ops here; the consuming code supplies real
 * implementations via `.provide({...})`.
 */
export const eventRegistrationMachine = setup({
  types: {
    context: {} as Context,
    input: {} as Context,
    events: {} as EventRegistrationEvent,
  },
  actions: {
    notifySuccess: () => {},
    triggerRefresh: () => {},
    onRegistrationFailed: (_, _params: {error: unknown}) => {},
    stashPendingTrack: () => {},
    notifyMembersOnlyBlocked: () => {},
    assignRoute: assign(({event}) => {
      if (event.type !== 'SELECT_ROUTE') return {};
      return {routeId: event.routeId, categoryId: event.categoryId};
    }),
    assignCategory: assign(({event}) => {
      if (event.type !== 'SELECT_CATEGORY') return {};
      return {categoryId: event.categoryId};
    }),
  },
  actors: {
    register: fromPromise(({input}: {input: RegisterInput}) =>
      registerForEvent({data: input})
    ),
    track: fromPromise(({input}: {input: TrackInput}) =>
      markAttending({data: input})
    ),
  },
  guards: {
    profileComplete: ({context}) =>
      context.profile != null &&
      hasRegistrationInfo(context.profile, context.skipRiderFields),
    /**
     * Only `true` when we're positively certain the user has cleared all
     * prerequisites: either the event has none, or the loader explicitly
     * returned `allSatisfied: true`. Treating `null` (unknown) as satisfied
     * is unsafe — that value can appear when the loader runs without an
     * authenticated profile, and routing straight to `submitting` in that
     * case makes the server throw the "complete prerequisites" error
     * instead of showing the self-attestation modal.
     */
    prerequisitesSatisfied: ({context}) =>
      context.prerequisiteStatus.prerequisites.length === 0 ||
      context.prerequisiteStatus.allSatisfied === true,
    isAuthenticated: ({context}) => context.profile != null,
    isTrackIntent: ({context}) => context.intent === 'track',
    /**
     * REGISTER is a no-op when the event requires membership and the rider is
     * not a current member. The panel renders a "Members only" affordance, so
     * REGISTER only ever arrives here via the post-login auto-replay — the
     * matched transition fires `notifyMembersOnlyBlocked` so a freshly-signed-up
     * rider gets a toast explaining why nothing happened, instead of the
     * profile-completion modal opening on a flow that can never succeed.
     */
    membersOnlyBlocked: ({context}) =>
      context.requiresClubMembership && !context.isMember,
  },
}).createMachine({
  id: 'eventRegistration',
  context: ({input}) => input,
  type: 'parallel',
  on: {
    SELECT_ROUTE: {actions: 'assignRoute'},
    SELECT_CATEGORY: {actions: 'assignCategory'},
  },
  states: {
    flow: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            REGISTER: [
              {guard: 'membersOnlyBlocked', actions: 'notifyMembersOnlyBlocked'},
              {
                target: 'submitting',
                guard: ({context}) =>
                  context.profile != null &&
                  hasRegistrationInfo(
                    context.profile,
                    context.skipRiderFields
                  ) &&
                  (context.prerequisiteStatus.prerequisites.length === 0 ||
                    context.prerequisiteStatus.allSatisfied === true),
              },
              {
                target: 'needsPrerequisiteConfirm',
                guard: 'profileComplete',
              },
              {target: 'needsProfile'},
            ],
            TRACK: [
              {
                target: 'needsAuth',
                guard: ({context}) => context.profile == null,
              },
              {target: 'submitting'},
            ],
          },
        },
        needsAuth: {
          entry: 'stashPendingTrack',
        },
        needsProfile: {
          on: {
            PROFILE_COMPLETED: [
              {target: 'submitting', guard: 'prerequisitesSatisfied'},
              {target: 'needsPrerequisiteConfirm'},
            ],
            CANCEL_PROFILE: 'idle',
          },
        },
        needsPrerequisiteConfirm: {
          on: {
            PREREQUISITES_CONFIRMED: {
              target: 'submitting',
              actions: assign({offPlatformClaim: true}),
            },
            CANCEL_PREREQUISITES: 'idle',
          },
        },
        /**
         * `submitting` is itself a substate machine: `dispatch` immediately
         * picks the `track` or `register` actor based on the `intent` context
         * value, so the right server call runs without callers needing to
         * know which to invoke.
         */
        submitting: {
          initial: 'dispatch',
          states: {
            dispatch: {
              always: [
                {target: 'track', guard: 'isTrackIntent'},
                {target: 'register'},
              ],
            },
            register: {
              invoke: {
                src: 'register',
                input: ({context}) => ({
                  eventId: context.eventId,
                  categoryId: context.categoryId,
                  routeId: context.routeId,
                  offPlatformClaim: context.offPlatformClaim,
                }),
                onDone: {
                  target: '#eventRegistration.flow.refreshing',
                  actions: 'notifySuccess',
                },
                onError: {
                  target: '#eventRegistration.flow.idle',
                  actions: {
                    type: 'onRegistrationFailed',
                    params: ({event}) => ({error: event.error}),
                  },
                },
              },
            },
            track: {
              invoke: {
                src: 'track',
                input: ({context}) => ({eventId: context.eventId}),
                onDone: {
                  target: '#eventRegistration.flow.refreshing',
                  actions: ['notifySuccess', raise({type: 'OPEN_HANDOFF'})],
                },
                onError: {
                  target: '#eventRegistration.flow.idle',
                  actions: {
                    type: 'onRegistrationFailed',
                    params: ({event}) => ({error: event.error}),
                  },
                },
              },
            },
          },
        },
        // `refreshing` is an optimistic post-submit state. We trigger a loader
        // invalidation on entry, then wait for the surrounding route wrapper to
        // observe `isRegistered` flipping true on the loader and dispatch
        // `REGISTRATION_CONFIRMED` — only then do we return to `idle`. Without
        // this wait the UI flashes "Register" between the router invalidate
        // resolving and React re-rendering with the new loader data.
        refreshing: {
          entry: 'triggerRefresh',
          on: {
            REGISTRATION_CONFIRMED: 'idle',
          },
        },
      },
    },
    handoffModal: {
      initial: 'closed',
      states: {
        closed: {
          on: {OPEN_HANDOFF: 'open'},
        },
        open: {
          on: {CLOSE_HANDOFF: 'closed'},
        },
      },
    },
  },
});
